import React from 'react';
import { Server, Cpu, HardDrive, Wifi } from 'lucide-react';
import { Card, MiniBar } from './primitives';
import { cpuColor, memColor, bwColor } from '../topology';
import { useTopology } from '../TopologyContext';

// Detailed per-node card showing CPU, RAM, 40G throughput, disk, uptime, and
// cluster activity. clusterId / clusterCount are passed in by the parent
// section so this stays a pure presentational component.
// Compact label for a long run name, e.g.
// "M0p83_Alpha1p50_nacelle_01-...-run021" -> "M0p83 · run021".
function shortJob(name) {
  if (!name) return '';
  const mach = name.match(/M0p\d+/);
  const run = name.match(/run\d+/i);
  const bits = [mach && mach[0], run && run[0]].filter(Boolean);
  return bits.length ? bits.join(' · ') : (name.length > 16 ? name.slice(0, 16) + '…' : name);
}

export function NodeCard({ node, data, network40g, nodeUsage, clusterId, clusterCount, isMobile }) {
  const { NODES_WITH_40G, CLUSTERS } = useTopology();
  const on = !data.error;
  const cpu = data.cpu_usage || 0;
  const memUsage = data.mem_usage || 0;
  const usedCores = data.used_cores ?? 0;
  const totalCores = data.cpu_cores ?? 0;
  const usedMem = data.used_mem ?? 0;
  const totalMem = data.total_mem ?? 0;

  const has40g = NODES_WITH_40G.has(node);
  const netData = has40g && network40g?.[node]?.status === 'ok' ? network40g[node] : null;
  const totalGbps = netData?.total_gbps ?? 0;

  const cluster = clusterId ? CLUSTERS[clusterId] : null;
  const isHead = cluster && cluster.head === node;

  // Live occupancy: IN USE (running a job) / FREE / POST-PROC (dedicated node17).
  const usage = nodeUsage?.[node] || {};
  const busy = !!usage.busy;
  const postproc = usage.role === 'postproc';
  const jobLabel = usage.job ? shortJob(usage.job) : null;

  // Placeholders for backend endpoints not yet built
  const uptime = data.uptime || null;
  const loadavg = data.load_avg || null;
  const diskUsed = data.disk_used_pct ?? null;
  const diskIo = data.disk_io_mbps ?? null;

  if (!on) {
    return (
      <Card style={{ padding: isMobile ? 16 : 20, border: '1px solid #7f1d1d' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server style={{ width: 16, height: 16, color: '#ef4444' }} />
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
              {node.toUpperCase()}
            </span>
          </div>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
        </div>
        <p style={{ color: '#ef4444', fontSize: 13 }}>Offline</p>
        {data.error && (
          <p style={{
            color: '#7f1d1d', fontSize: 11, marginTop: 6,
            fontFamily: 'monospace', wordBreak: 'break-all',
          }}>
            {String(data.error).slice(0, 80)}
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card style={{ padding: isMobile ? 16 : 20 }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Server style={{ width: 16, height: 16, color: '#3b82f6' }} />
          <span style={{
            color: '#fff', fontWeight: 600, fontSize: 14, letterSpacing: 0.5,
          }}>{node.toUpperCase()}</span>
          {has40g && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: '#22d3ee',
              backgroundColor: 'rgba(34,211,238,0.12)',
              padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5,
            }}>40G</span>
          )}
          {isHead && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: '#fbbf24',
              backgroundColor: 'rgba(251,191,36,0.12)',
              padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5,
            }}>HEAD</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {busy ? (
            <span title={usage.job || 'running a job'} style={{
              fontSize: 10, fontWeight: 700, color: '#22c55e',
              backgroundColor: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.4)',
              padding: '2px 8px', borderRadius: 6, letterSpacing: 0.3,
              whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>IN USE{jobLabel ? ` · ${jobLabel}` : ''}</span>
          ) : postproc ? (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#f59e0b',
              backgroundColor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)',
              padding: '2px 8px', borderRadius: 6, letterSpacing: 0.3,
            }}>POST-PROC</span>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#64748b',
              backgroundColor: 'rgba(100,116,139,0.12)', border: '1px solid rgba(100,116,139,0.3)',
              padding: '2px 8px', borderRadius: 6, letterSpacing: 0.3,
            }}>FREE</span>
          )}
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: busy ? '#22c55e' : '#334155',
          }} />
        </div>
      </div>

      {/* CPU bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 4,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Cpu style={{ width: 12, height: 12, color: '#9ca3af' }} />
            <span style={{ color: '#9ca3af', fontSize: 11 }}>CPU</span>
          </div>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
            {cpu.toFixed(0)}%
            <span style={{ color: '#6b7280', fontSize: 10, marginLeft: 6 }}>
              {usedCores.toFixed(0)}/{totalCores}
            </span>
          </span>
        </div>
        <MiniBar percent={cpu} color={cpuColor(cpu)} />
      </div>

      {/* Memory bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 4,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <HardDrive style={{ width: 12, height: 12, color: '#9ca3af' }} />
            <span style={{ color: '#9ca3af', fontSize: 11 }}>RAM</span>
          </div>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
            {memUsage.toFixed(0)}%
            <span style={{ color: '#6b7280', fontSize: 10, marginLeft: 6 }}>
              {usedMem.toFixed(0)}/{totalMem.toFixed(0)} GB
            </span>
          </span>
        </div>
        <MiniBar percent={memUsage} color={memColor(memUsage)} />
      </div>

      {/* Network throughput (40G nodes only) */}
      {has40g && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', marginBottom: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Wifi style={{ width: 12, height: 12, color: '#9ca3af' }} />
              <span style={{ color: '#9ca3af', fontSize: 11 }}>40G Fabric</span>
            </div>
            {netData ? (
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                {totalGbps.toFixed(1)}
                <span style={{ color: '#6b7280', fontSize: 10, marginLeft: 4 }}>Gb/s</span>
              </span>
            ) : (
              <span style={{ color: '#4b5563', fontSize: 11 }}>—</span>
            )}
          </div>
          <MiniBar percent={(totalGbps / 40) * 100} color={bwColor(totalGbps)} />
          {netData && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginTop: 4,
              fontSize: 10, color: '#6b7280',
            }}>
              <span>RX {netData.rx_gbps?.toFixed(1) ?? '0.0'}</span>
              <span>TX {netData.tx_gbps?.toFixed(1) ?? '0.0'}</span>
            </div>
          )}
        </div>
      )}

      {/* Disk + uptime/load row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 8, paddingTop: 12, marginTop: 4,
        borderTop: '1px solid #1e293b', fontSize: 11,
      }}>
        <div>
          <p style={{ color: '#6b7280', marginBottom: 2, fontSize: 10 }}>DISK</p>
          <p style={{ color: diskUsed != null ? '#d1d5db' : '#4b5563' }}>
            {diskUsed != null ? `${diskUsed}% used` : '—'}
          </p>
          <p style={{ color: diskIo != null ? '#9ca3af' : '#374151', fontSize: 10, marginTop: 2 }}>
            {diskIo != null ? `${diskIo} MB/s` : '—'}
          </p>
        </div>
        <div>
          <p style={{ color: '#6b7280', marginBottom: 2, fontSize: 10 }}>UPTIME</p>
          <p style={{ color: uptime ? '#d1d5db' : '#4b5563' }}>{uptime || '—'}</p>
          <p style={{ color: loadavg ? '#9ca3af' : '#374151', fontSize: 10, marginTop: 2 }}>
            {loadavg ? `load ${loadavg}` : '—'}
          </p>
        </div>
      </div>

      {/* Cluster activity summary */}
      {cluster && clusterCount && (clusterCount.running > 0 || clusterCount.pending > 0) && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
          <p style={{ color: '#6b7280', fontSize: 10, marginBottom: 6 }}>
            {cluster.label.toUpperCase()} ACTIVITY
          </p>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', fontSize: 11,
          }}>
            {clusterCount.running > 0 && (
              <span style={{ color: '#22c55e' }}>
                <span style={{ fontWeight: 700 }}>{clusterCount.running}</span> running
              </span>
            )}
            {clusterCount.pending > 0 && (
              <span style={{ color: '#eab308' }}>
                <span style={{ fontWeight: 700 }}>{clusterCount.pending}</span> queued
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
